import { API_CONFIG, SYSTEM_PROMPT } from '../constants';
import { APIError } from '../utils/errors';

interface ChatResponse {
  choices?: Array<{
    message?: {
      content?: string;
    };
  }>;
  error?: {
    message?: string;
  };
}

/**
 * Sends a chat message to the API
 * @param userMessage - The user's message
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to the AI response content
 * @throws APIError if the request fails
 */
export async function sendChatMessage(
  userMessage: string,
  signal?: AbortSignal
): Promise<string> {
  const payload = {
    model: API_CONFIG.MODEL,
    messages: [
      {
        role: 'system' as const,
        content: SYSTEM_PROMPT,
      },
      {
        role: 'user' as const,
        content: userMessage,
      },
    ],
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

    // Use provided signal or controller signal
    const abortSignal = signal || controller.signal;

    const response = await fetch(API_CONFIG.ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(import.meta.env.VITE_API_KEY && {
          Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
        }),
      },
      body: JSON.stringify(payload),
      signal: abortSignal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData: ChatResponse = await response.json().catch(() => ({}));
      throw new APIError(
        errorData?.error?.message || 'API request failed',
        response.status,
        errorData
      );
    }

    const data: ChatResponse = await response.json();
    const content = data.choices?.[0]?.message?.content;

    if (!content) {
      throw new APIError('Invalid response format', response.status, data);
    }

    return content;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw new APIError('Request timeout', 408);
    }

    throw new APIError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}
